<!-- // Table -->
<script lang="ts">
    import { get } from "lodash"
    import { getOperation } from "../../../api"
    import { operationSources } from "../../../config"
    import { openOverlay, showPrices } from "../../../store"
    import DateField from "../../widgets/fields/DateField.svelte"
    import DealerField from "../../widgets/fields/DealerField.svelte"
    import NumberField from "../../widgets/fields/NumberField.svelte"
    import OperationNumberField from "../../widgets/fields/OperationNumberField.svelte"
    import { _ } from "svelte-i18n"
    import SourceField from "../../widgets/fields/SourceField.svelte"
    import StatusField from "../../widgets/fields/StatusField.svelte"
    import TextField from "../../widgets/fields/TextField.svelte"
    import MiniLoader from "../../widgets/MiniLoader.svelte"

    import Table from "../../widgets/Table.svelte"

    export let operation: Operation | OperationOut
    export let position: OperationPosition
    export let connectionsExit: boolean = false
    // Die Tabelle führt entweder Aufträge oder Angebote (nie gemischt),
    // je nach connectionsExit. TableRows erlaubt genau diesen Union.
    let rows: TableRows = []
    // Konkret typisierte Puffer je Collection, damit kein Cast nötig ist.
    let rowsIn: Operation[] = []
    let rowsOut: OperationOut[] = []

    let filter: any

    $: if (operation) {
        if (connectionsExit) {
            filter = {
                refNumber: operation.number,
            }
        } else {
            if (operation.collectionName == "operation") {
                filter = {
                    positions: {
                        $elemMatch: {
                            "origin.operationNumber": operation.number,
                        },
                    },
                }
                if (position) {
                    filter["positions"]["$elemMatch"]["origin.positionNumber"] = position.number
                }
            } else {
                filter = {
                    positions: {
                        $elemMatch: {
                            "operationOutOfferReference.id": operation.id,
                        },
                    },
                }
            }
        }
    }

    let loading = false
    let showMoreButton = false
    let offset = 0
    let limit = 10
    let sort = "-number"

    let abortController: AbortController

    /**
     * Nummer der Position, die auf `position` verweist (origin.positionNumber).
     * Wird sowohl für die Tabellenspalte als auch beim Klick auf eine Zeile
     * gebraucht.
     */
    function findePositionsnummer(positionen: OperationPosition[]): number {
        let nummer: number
        positionen?.forEach((p) => {
            if (p.origin?.positionNumber == position?.number) {
                nummer = p.number
            }
        })
        return nummer
    }

    function load() {
        if (abortController) {
            abortController.abort() // cancel all running requests
        }
        abortController = new AbortController()
        const { signal } = abortController

        loading = true
        // Beide Zweige laden in dieselbe Tabelle, aber aus verschiedenen
        // Collections (Auftrag vs. Angebot). Jeder Zweig führt seinen eigenen
        // konkret typisierten Zeilenpuffer, damit nichts gecastet werden muss.
        if (connectionsExit) {
            getOperation<OperationOut[]>("operation-out", filter, limit, offset, "list", sort, signal)
                .then((r) => {
                    rowsOut = [...rowsOut, ...(r?.data || [])]
                    rows = rowsOut
                    offset += limit
                    showMoreButton = limit == r?.data?.length
                })
                .catch((e) => {
                    console.log(e)
                })
                .finally(() => {
                    loading = false
                })
        } else {
            getOperation<Operation[]>("operation", filter, limit, offset, "list", sort, signal)
                .then((r) => {
                    rowsIn = [...rowsIn, ...(r?.data || [])]
                    rows = rowsIn
                    offset += limit
                    showMoreButton = limit == r?.data?.length
                })
                .catch((e) => {
                    console.log(e)
                })
                .finally(() => {
                    loading = false
                })
        }
    }

    $: if (filter || sort) {
        offset = 0
        rows = []
        rowsIn = []
        rowsOut = []
        load()
    }
</script>

{#if rows?.length}
    <Table
        bind:sort
        columns="{[
            {
                label: $_('positions.origin'),
                field: 'source',
                sortable: true,
                className: 'herkunft hidden430 w-70 mw-70',
                align: 'center',
                component: SourceField,
                getProps(row, field) {
                    return {
                        value: row.source,
                    }
                },
                tooltipComponent: TextField,
                getTooltipProps(row, field) {
                    return {
                        value: 'Herkunft: ' + operationSources[row.source]?.label,
                    }
                },
            },
            {
                label: $_('positions.customerNumber'),
                field: 'dealerNumber',
                sortable: true,
                className: 'mw-90 w-90 hidden1100',
                tooltipComponent: DealerField,
                getTooltipProps(op, field) {
                    return {
                        number: op.dealerNumber,
                    }
                },
            },
            {
                label: $_('positions.attachement'),
                field: 'number',
                sortable: true,
                className: 'mw-160 w-160',
                component: OperationNumberField,
                getProps(operation, field) {
                    return {
                        operation,
                        positionNumber: findePositionsnummer(operation.positions),
                    }
                },
            },
            {
                label: $_('positions.amountOfPositions'),
                field: 'countOfPositions',
                sortable: true,
                className: 'mw-90 w-90 hidden1830',
                align: 'center',
            },
            {
                label: $_('positions.status'),
                field: 'statusSummary',
                className: 'status w-170 mw-170',
                align: 'left',
                component: StatusField,
                getProps(row, field) {
                    return {
                        row,
                    }
                },
            },
            {
                label: $_('positions.date'),
                field: 'releaseDate',
                sortable: true,
                className: 'mw-100 w-100 hidden860',
                component: DateField,
                getProps(row, field) {
                    return {
                        value: get(row, field),
                    }
                },
            },
            {
                label: $_('positions.inquiryOrOrderNumber'),
                field: 'inquiryNumber',
                sortable: true,
                className: 'mw-120 w-120 hidden1600',
            },
            {
                label: $_('positions.commission'),
                field: 'commission',
                sortable: true,
                className: 'mw-160 w-160 hidden680',
            },
            {
                label: $_('positions.constructionProject'),
                field: 'constructionProject',
                sortable: true,
                className: 'mw-160 w-160 hidden980',
            },
            {
                label: $_('positions.constructionSite'),
                field: 'constructionSite',
                sortable: true,
                className: 'mw-100 w-100 hidden1430',
            },
            {
                label: $_('positions.residentialUnits'),
                field: 'residentialUnitsCount',
                sortable: true,
                className: 'mw-70 w-70 hidden1730',
                align: 'center',
                tooltipComponent: TextField,
                getTooltipProps(row, field) {
                    return {
                        value: `${row.residentialUnitsCount} ${$_('positions.residentialUnitsCountTooltip')}`,
                    }
                },
            },
            {
                hide: !$showPrices,
                label: $_('positions.sum'),
                field: 'sumNetPrice',
                sortable: true,
                className: 'mw-100 w-100 hidden1180',
                align: 'right',
                component: NumberField,
                getProps(row, field) {
                    return {
                        value: get(row, field),
                        unit: '€',
                    }
                },
                tooltipComponent: TextField,
                getTooltipProps(row, field) {
                    return {
                        value:
                            $_('positions.net') +
                            (row.type == 1 ? ` ${$_('positions.withoutAlternativePositions')}` : ''),
                    }
                },
            },
        ]}"
        {rows}
        on:click="{(r) => {
            openOverlay(r.detail, 'details', {
                activePosition: findePositionsnummer(r.detail.positions),
            })
        }}"
    />
{:else if !loading}
    {$_("positions.noAttachedOperations")}
{/if}

{#if loading}
    <div class="centerbutton">
        <br />
        <br />
        <MiniLoader />
    </div>
{/if}

{#if showMoreButton && !loading}
    <!-- Load more -->
    <div class="centerbutton">
        <button class="btn loadmore" on:click="{load}"> {$_("general.loadMore")}</button>
    </div>
{/if}
